<template>
  <div class="pivot-map">
    <div class="pivot-map-toolbar">
      <button type="button" class="pivot-map-zoom-btn" @click="zoomOut" :disabled="zoom <= MIN_ZOOM" title="Diminuir zoom">−</button>
      <span class="pivot-map-zoom-level">{{ Math.round(zoom * 100) }}%</span>
      <button type="button" class="pivot-map-zoom-btn" @click="zoomIn" :disabled="zoom >= MAX_ZOOM" title="Aumentar zoom">+</button>
      <button type="button" class="pivot-map-zoom-btn pivot-map-zoom-reset" v-if="zoom !== 1" @click="resetZoom">Ajustar</button>
    </div>
    <!-- Wrap com scroll nativo: em zoom > 100% o SVG fica maior que a área visível
         e o usuário navega arrastando (ver startPan/doPan) ou pela barra de rolagem —
         evita reimplementar pan com matriz de transformação manual. -->
    <div ref="wrap" class="pivot-map-svg-wrap" :class="{ 'pivot-map-svg-wrap--zoomed': zoom > 1 }"
      :style="{ maxWidth: maxWidth + 'px' }"
      @mousedown="startPan" @mousemove="doPan" @mouseup="endPan" @mouseleave="endPan">
      <svg :viewBox="`0 0 ${vb} ${vb}`" :style="{ width: zoom * 100 + '%', height: zoom * 100 + '%' }" preserveAspectRatio="xMidYMid meet">
        <!-- área da circunferência: molhada (passou pressurizado), seca (passou sem
             pressão) ou não percorrida nesse dia — desenhada antes dos anéis/trilha
             pra ficar por baixo, com pouca opacidade (issue #437). -->
        <path v-for="(run, i) in sectorRuns" :key="`sec-${i}`" :d="wedgePath(run.start, run.end)"
          :class="['pivot-map-sector', `pivot-map-sector--${run.state}`]" />

        <!-- anéis concêntricos (metros a partir do centro) -->
        <circle v-for="ring in rings" :key="`r-${ring}`" :cx="c" :cy="c" :r="scale * ring" class="pivot-map-ring" />
        <text v-for="ring in rings" :key="`rl-${ring}`" :x="c + scale * ring + 3" :y="c + 3" class="pivot-map-ring-label">
          {{ Math.round(ring) }}m
        </text>

        <!-- raios a cada 45° -->
        <g v-for="deg in spokes" :key="`s-${deg}`">
          <line :x1="c" :y1="c" :x2="spokeX(deg)" :y2="spokeY(deg)" class="pivot-map-spoke" />
          <text :x="spokeLabelX(deg)" :y="spokeLabelY(deg)" class="pivot-map-deg-label" text-anchor="middle">
            {{ deg }}°
          </text>
        </g>

        <!-- raio total do pivô -->
        <circle :cx="c" :cy="c" :r="scale * radius" class="pivot-map-radius" />

        <!-- trilha: um segmento de reta por par de pontos consecutivos, colorido
             conforme os dois extremos estarem pressurizados ou não -->
        <line v-for="(seg, i) in segments" :key="`seg-${i}`" :x1="toX(seg.a.east)" :y1="toY(seg.a.north)"
          :x2="toX(seg.b.east)" :y2="toY(seg.b.north)" :class="['pivot-map-trail', seg.pressurized ? 'pivot-map-trail--pressurized' : '']" />

        <circle v-for="(p, i) in points" :key="i" :cx="toX(p.east)" :cy="toY(p.north)" r="3.4"
          :class="['pivot-map-point', p.pressurized ? 'pivot-map-point--pressurized' : '']" />

        <!-- início/fim do dia em destaque: desenhados por cima da trilha e dos
             pontos comuns (senão ficam escondidos quando o pivô cruza o próprio
             caminho) — início é um anel vazado, fim é o ponto maciço maior/escuro
             já usado antes, agora com rótulo junto. -->
        <g v-if="firstPoint" class="pivot-map-endpoint pivot-map-endpoint--first">
          <circle :cx="toX(firstPoint.east)" :cy="toY(firstPoint.north)" />
          <text :x="toX(firstPoint.east)" :y="toY(firstPoint.north) - 9" text-anchor="middle">Início</text>
        </g>
        <g v-if="lastPoint" :class="['pivot-map-endpoint', 'pivot-map-endpoint--last', lastPoint.pressurized ? 'pivot-map-endpoint--pressurized' : '']">
          <circle :cx="toX(lastPoint.east)" :cy="toY(lastPoint.north)" />
          <text :x="toX(lastPoint.east)" :y="toY(lastPoint.north) - 9" text-anchor="middle">Fim</text>
        </g>

        <!-- centro -->
        <circle :cx="c" :cy="c" r="4" class="pivot-map-center" />
        <text :x="c" :y="c + 18" class="pivot-map-center-label" text-anchor="middle">Centro</text>
      </svg>
    </div>

    <div class="pivot-map-legend">
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-swatch pivot-map-legend-swatch--normal"></i>Movimento sem pressão</span>
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-swatch pivot-map-legend-swatch--pressurized"></i>Movimento pressurizado</span>
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-swatch pivot-map-legend-swatch--area-wet"></i>Área molhada</span>
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-swatch pivot-map-legend-swatch--area-dry"></i>Área seca</span>
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-swatch pivot-map-legend-swatch--area-none"></i>Não percorrida</span>
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-dot pivot-map-legend-dot--start"></i>Início do dia</span>
      <span class="pivot-map-legend-item"><i class="pivot-map-legend-dot pivot-map-legend-dot--end"></i>Leitura mais recente</span>
    </div>

    <div class="pivot-map-stats">
      <div class="pivot-map-stat">
        <div class="pivot-map-stat-label">Rotação líquida no dia</div>
        <div class="pivot-map-stat-value">{{ formatDeg(stats.netRotation) }}</div>
      </div>
      <div class="pivot-map-stat">
        <div class="pivot-map-stat-label">Ângulo inicial → final</div>
        <div class="pivot-map-stat-value">
          {{ stats.startAngle === null ? "—" : `${Math.round(stats.startAngle)}° → ${Math.round(stats.endAngle)}°` }}
        </div>
      </div>
      <div class="pivot-map-stat">
        <div class="pivot-map-stat-label">Duração do log</div>
        <div class="pivot-map-stat-value">{{ stats.durationMin }} min</div>
      </div>
      <div class="pivot-map-stat">
        <div class="pivot-map-stat-label">Tempo pressurizado</div>
        <div class="pivot-map-stat-value">{{ stats.pressurizedMin }} min</div>
      </div>
      <div class="pivot-map-stat">
        <div class="pivot-map-stat-label">Registros</div>
        <div class="pivot-map-stat-value">{{ stats.count }}</div>
      </div>
    </div>

    <div class="pivot-map-caption">
      Anéis em metros a partir do centro do pivô (linha tracejada = raio total, ~{{ Math.round(radius) }} m).
      Trilha azul = trecho percorrido com o sistema pressurizado; trilha cinza =
      movimento sem pressão. Anel vazado "Início" = primeira leitura do dia; ponto
      escuro "Fim" = leitura mais recente.
    </div>
  </div>
</template>

<script>
import { sectorStates, sectorRuns } from "../pirGeo";

const SPOKES = [0, 45, 90, 135, 180, 225, 270, 315];

export default {
  name: "pivot-trajectory-map",
  props: {
    points: { type: Array, default: () => [] },
    radius: { type: Number, default: 1 },
    stats: {
      type: Object,
      default: () => ({ netRotation: 0, startAngle: null, endAngle: null, durationMin: 0, pressurizedMin: 0, count: 0 }),
    },
    // Teto de largura do mapa (px) — o quadrado cresce com o container (aspect-ratio
    // 1:1 no CSS) até esse limite, em vez de ficar preso a uma altura fixa que
    // desperdiçava boa parte da largura disponível no painel (issue: "valores muito
    // pequenos" no gráfico de trajeto).
    maxWidth: { type: Number, default: 640 },
  },
  data() {
    return {
      vb: 400,
      spokes: SPOKES,
      zoom: 1,
      MIN_ZOOM: 1,
      MAX_ZOOM: 3,
      panning: false,
      panStart: null,
    };
  },
  computed: {
    c() {
      return this.vb / 2;
    },
    // Margem além do raio, pra trilha e rótulos não colarem na borda do viewBox.
    extent() {
      return Math.max(this.radius, 1) * 1.18;
    },
    scale() {
      return (this.vb / 2 - 28) / this.extent;
    },
    rings() {
      const step = this.niceStep(this.extent / 4);
      const list = [];
      for (let v = step; v <= this.extent; v += step) list.push(v);
      return list;
    },
    firstPoint() {
      return this.points.length ? this.points[0] : null;
    },
    // Com um único ponto no dia, início e fim são o mesmo lugar -- omite o
    // marcador de "fim" pra não empilhar os dois rótulos um em cima do outro.
    lastPoint() {
      return this.points.length > 1 ? this.points[this.points.length - 1] : null;
    },
    segments() {
      const segs = [];
      for (let i = 1; i < this.points.length; i++) {
        const a = this.points[i - 1];
        const b = this.points[i];
        segs.push({ a, b, pressurized: a.pressurized && b.pressurized });
      }
      return segs;
    },
    sectorRuns() {
      const runs = sectorRuns(sectorStates(this.points));
      // Arco SVG não desenha um círculo completo num único comando (ponto
      // inicial e final coincidem) — se o dia inteiro caiu num só estado
      // (ex: pivô deu a volta completa), parte em duas metades de 180°.
      if (runs.length === 1 && runs[0].end - runs[0].start === 360) {
        const { start, state } = runs[0];
        return [
          { start, end: start + 180, state },
          { start: start + 180, end: start + 360, state },
        ];
      }
      return runs;
    },
  },
  methods: {
    toX(east) {
      return this.c + east * this.scale;
    },
    toY(north) {
      // SVG cresce pra baixo — Norte precisa ir pra cima.
      return this.c - north * this.scale;
    },
    // Fatia de pizza do centro até o raio total do pivô, entre dois graus em
    // convenção bússola — mesma conversão sin/cos usada pelos raios (spokeX/Y).
    wedgePath(startDeg, endDeg) {
      const r = this.scale * this.radius;
      const x1 = this.c + r * Math.sin((startDeg * Math.PI) / 180);
      const y1 = this.c - r * Math.cos((startDeg * Math.PI) / 180);
      const x2 = this.c + r * Math.sin((endDeg * Math.PI) / 180);
      const y2 = this.c - r * Math.cos((endDeg * Math.PI) / 180);
      const largeArc = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${this.c} ${this.c} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    },
    spokeX(deg) {
      return this.c + (this.vb / 2 - 20) * Math.sin((deg * Math.PI) / 180);
    },
    spokeY(deg) {
      return this.c - (this.vb / 2 - 20) * Math.cos((deg * Math.PI) / 180);
    },
    spokeLabelX(deg) {
      return this.c + (this.vb / 2 - 8) * Math.sin((deg * Math.PI) / 180);
    },
    spokeLabelY(deg) {
      return this.c - (this.vb / 2 - 8) * Math.cos((deg * Math.PI) / 180) + 3;
    },
    niceStep(raw) {
      const pow = Math.pow(10, Math.floor(Math.log10(raw || 1)));
      const candidates = [1, 2, 5, 10].map((m) => m * pow);
      return candidates.find((c) => c >= raw) || candidates[candidates.length - 1];
    },
    formatDeg(v) {
      const rounded = Math.round(v);
      return `${rounded > 0 ? "+" : ""}${rounded}°`;
    },
    zoomIn() {
      this.zoom = Math.min(this.MAX_ZOOM, +(this.zoom + 0.5).toFixed(2));
    },
    zoomOut() {
      this.zoom = Math.max(this.MIN_ZOOM, +(this.zoom - 0.5).toFixed(2));
      if (this.zoom === this.MIN_ZOOM) this.resetScroll();
    },
    resetZoom() {
      this.zoom = this.MIN_ZOOM;
      this.resetScroll();
    },
    resetScroll() {
      this.$nextTick(() => {
        if (this.$refs.wrap) {
          this.$refs.wrap.scrollLeft = 0;
          this.$refs.wrap.scrollTop = 0;
        }
      });
    },
    // Arrasta pra navegar quando ampliado — usa o scroll nativo do wrap em vez de
    // uma matriz de transformação própria, então não precisa de lógica de clamping.
    startPan(e) {
      if (this.zoom <= this.MIN_ZOOM) return;
      this.panning = true;
      this.panStart = { x: e.clientX, y: e.clientY, left: this.$refs.wrap.scrollLeft, top: this.$refs.wrap.scrollTop };
    },
    doPan(e) {
      if (!this.panning) return;
      this.$refs.wrap.scrollLeft = this.panStart.left - (e.clientX - this.panStart.x);
      this.$refs.wrap.scrollTop = this.panStart.top - (e.clientY - this.panStart.y);
    },
    endPan() {
      this.panning = false;
    },
  },
};
</script>

<style scoped>
.pivot-map {
  width: 100%;
}
.pivot-map-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 6px;
}
.pivot-map-zoom-btn {
  width: 26px;
  height: 26px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  color: #555;
  font-size: 1.05em;
  line-height: 1;
  cursor: pointer;
}
.pivot-map-zoom-btn:hover:not(:disabled) {
  background: #f5f6f5;
}
.pivot-map-zoom-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.pivot-map-zoom-reset {
  width: auto;
  padding: 0 8px;
  font-size: 0.72em;
}
.pivot-map-zoom-level {
  font-size: 0.72em;
  color: #888;
  min-width: 32px;
  text-align: center;
}
/* Quadrado que cresce com a largura do container (até maxWidth) em vez de ficar
   preso a uma altura fixa — era isso que deixava o mapa pequeno em painéis largos. */
.pivot-map-svg-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  margin: 0 auto;
  overflow: auto;
}
.pivot-map-svg-wrap svg {
  display: block;
}
.pivot-map-svg-wrap--zoomed {
  cursor: grab;
}
.pivot-map-svg-wrap--zoomed:active {
  cursor: grabbing;
}
.pivot-map-sector {
  stroke: none;
}
.pivot-map-sector--wet {
  fill: rgba(41, 138, 190, 0.16);
}
.pivot-map-sector--dry {
  fill: rgba(198, 149, 46, 0.16);
}
.pivot-map-sector--none {
  fill: rgba(0, 0, 0, 0.035);
}
.pivot-map-ring {
  fill: none;
  stroke: #e2e2e2;
  stroke-width: 1;
}
.pivot-map-ring-label {
  font-size: 8px;
  fill: #aaa;
}
.pivot-map-spoke {
  stroke: #ececec;
  stroke-width: 1;
}
.pivot-map-deg-label {
  font-size: 9px;
  fill: #999;
}
.pivot-map-radius {
  fill: none;
  stroke: #b7b7b7;
  stroke-width: 1;
  stroke-dasharray: 3 3;
}
.pivot-map-trail {
  stroke: #b9c2bd;
  stroke-width: 2;
  stroke-linecap: round;
  opacity: 0.8;
}
.pivot-map-trail--pressurized {
  stroke: #298abe;
  stroke-width: 2.6;
  opacity: 0.9;
}
.pivot-map-point {
  fill: #94c7a7;
  stroke: #fff;
  stroke-width: 0.6;
}
.pivot-map-point--pressurized {
  fill: #298abe;
}
.pivot-map-endpoint text {
  font-size: 8px;
  font-weight: 700;
  pointer-events: none;
}
.pivot-map-endpoint--first circle {
  r: 5.2;
  fill: #fff;
  stroke: #3d9161;
  stroke-width: 1.8;
}
.pivot-map-endpoint--first text {
  fill: #3d9161;
}
.pivot-map-endpoint--last circle {
  r: 5.2;
  fill: #245c3a;
  stroke: #fff;
  stroke-width: 1.2;
}
.pivot-map-endpoint--last text {
  fill: #245c3a;
}
.pivot-map-endpoint--last.pivot-map-endpoint--pressurized circle {
  fill: #1a5f80;
}
.pivot-map-endpoint--last.pivot-map-endpoint--pressurized text {
  fill: #1a5f80;
}
.pivot-map-center {
  fill: #dc3545;
}
.pivot-map-center-label {
  font-size: 10px;
  font-weight: 600;
  fill: #dc3545;
}
.pivot-map-legend {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 0.72em;
  color: #666;
  margin-top: 6px;
}
.pivot-map-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.pivot-map-legend-swatch {
  width: 14px;
  height: 3px;
  border-radius: 2px;
  display: inline-block;
}
.pivot-map-legend-swatch--normal {
  background: #b9c2bd;
}
.pivot-map-legend-swatch--pressurized {
  background: #298abe;
}
.pivot-map-legend-swatch--area-wet {
  background: rgba(41, 138, 190, 0.5);
}
.pivot-map-legend-swatch--area-dry {
  background: rgba(198, 149, 46, 0.5);
}
.pivot-map-legend-swatch--area-none {
  background: rgba(0, 0, 0, 0.15);
}
.pivot-map-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.pivot-map-legend-dot--start {
  background: #fff;
  border: 1.8px solid #3d9161;
}
.pivot-map-legend-dot--end {
  background: #245c3a;
}
.pivot-map-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 8px;
}
@media (min-width: 500px) {
  .pivot-map-stats {
    grid-template-columns: repeat(3, 1fr);
  }
}
.pivot-map-stat {
  background: #f5f6f5;
  border-radius: 6px;
  padding: 6px 8px;
}
.pivot-map-stat-label {
  font-size: 0.68em;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.pivot-map-stat-value {
  font-size: 1em;
  font-weight: 600;
  color: #333;
}
.pivot-map-caption {
  font-size: 0.72em;
  color: #888;
  margin-top: 8px;
  line-height: 1.4;
}
</style>
